import { HelperCard } from '@design-system/Cards/HelperCard';
import { ScrollArea, ScrollBar } from '@design-system/ScrollArea';
import { Sheet, SheetContent, SheetDescription } from '@design-system/Sheet';
import { useState } from 'react';
import { getBoostersAndBlockersImageUrl } from '@utils/nutrition/getImageUrl';
import { Image } from '@design-system/Image';

export interface Badge {
  description: string;
  image_url: string;
  title: string;
}

interface BadgeListProps {
  badgeInfo: Badge[];
  'data-testid'?: string;
}

export const NutritionBadgeDetails = ({ badgeInfo, 'data-testid': testId }: BadgeListProps) => {
  const [sheetData, setSheetData] = useState<Badge | null>(null);

  const handleCardClick = (badgeItem: Badge) => {
    setSheetData(badgeItem);
  };

  const handleCloseSheet = () => {
    setSheetData(null);
  };

  return (
    <div data-testid={testId}>
      <ScrollArea data-testid={`${testId}-scrollArea`} className="pt-12 pb-5">
        <div 
          data-testid={`${testId}-badgeGrid`}
          className="grid grid-flow-col gap-2 justify-start"
        >
          {badgeInfo?.map((badgeItem, index) => (
            <div 
              key={badgeItem.title} 
              data-testid={`${testId}-badgeContainer-${index}`}
              className="pr-2"
            >
              <HelperCard
                data-testid={`${testId}-badge-${index}`}
                title={badgeItem.title}
                image_url={getBoostersAndBlockersImageUrl(badgeItem.image_url)}
                onClick={() => handleCardClick(badgeItem)}
              />
            </div>
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {sheetData && (
        <Sheet open onOpenChange={(open) => !open && handleCloseSheet()}>
          <SheetContent 
            data-testid={`${testId}-modal`}
            variant="glass" 
            className="pt-20 px-8 pb-8"
          >
            <SheetDescription>
              <div 
                data-testid={`${testId}-modalContent`}
                className="flex flex-col"
              >
                {sheetData?.image_url && (
                  <div 
                    data-testid={`${testId}-modalImageContainer`}
                    className='flex justify-center'
                  >
                    <Image
                      data-testid={`${testId}-modalImage`}
                      className="w-[11.25rem] h-[11.25rem] object-cover"
                      src={getBoostersAndBlockersImageUrl(sheetData?.image_url)}
                      alt={sheetData?.title}
                    />
                  </div>
                )}
                <span 
                  data-testid={`${testId}-modalTitle`}
                  className="typography-h1 text-primary"
                >
                  {sheetData?.title}
                </span>
                <span 
                  data-testid={`${testId}-modalDescription`}
                  className="typography-body text-secondary pt-6"
                >
                  {sheetData?.description}
                </span>
              </div>
            </SheetDescription>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};
